import React, { useEffect, useRef } from "react";
import { DataTable, DataTableValue } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { TableProps } from "./type";

export function Table<T extends DataTableValue>({
  columns,
  data,
  title = "Table",
  onAddNew,
  loading = false,
  onRowClicked,
  onSelectionChange,
  selectionMode = "single",
  selection,
  extraHeader,
  loadMore,
  sortField: controlledSortField,
  sortOrder: controlledSortOrder,
  onSort,
}: TableProps<T>) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 1.0 },
    );

    const sentinel = sentinelRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [loadMore]);

  const headerContent = (
    <div className="flex justify-content-between align-items-center mb-2">
      <h2 className="text-xl font-semibold text-primary">{title}</h2>
      {onAddNew && (
        <Button
          label="Add New"
          icon="pi pi-plus"
          className="p-button-sm"
          onClick={onAddNew}
        />
      )}
    </div>
  );

  return (
    <div className="h-full overflow-hidden">
      <div>{headerContent}</div>
      {extraHeader && <div className="mb-2">{extraHeader}</div>}

      <div style={{ overflowY: "auto" }}>
        {selectionMode === "single" ? (
          <DataTable
            value={data}
            onRowClick={onRowClicked}
            className="p-datatable-xs shadow-sm border-round-sm"
            selection={selection as T}
            onSelectionChange={(e) => onSelectionChange?.(e.value as T | T[])}
            selectionMode="single"
            stripedRows
            {...(onSort ? { sortField: controlledSortField, sortOrder: controlledSortOrder, onSort, removableSort: true } : {})}
          >
            {columns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={col.sortable}
                body={col.body}
              />
            ))}
          </DataTable>
        ) : (
          <DataTable
            value={data}
            onRowClick={onRowClicked}
            className="p-datatable-xs shadow-sm border-round-sm"
            stripedRows
            selectionMode="multiple"
            selection={selection as T[]}
            onSelectionChange={(e) => onSelectionChange?.(e.value as T[])}
            rowHover
          >
            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
            {columns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={col.sortable}
                body={col.body}
              />
            ))}
          </DataTable>
        )}

        {/* Loader y sentinel */}
        {loading && (
          <div className="text-center mt-2 text-gray-500 animate-pulse">
            Loading more...
          </div>
        )}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </div>
  );
}
